/**
 * Lógica pura de «Comparar campañas»: el orden de la tabla, las etiquetas y los textos fijos.
 *
 * El orden: un clic en el título ordena por esa columna (los números de mayor a menor, los textos de la A a la Z) y otro
 * clic invierte. Lo que no tiene dato (sin clics medidos, sin medición, sin entregados) va SIEMPRE al final, en los dos
 * sentidos: arriba de todo, un «—» parecería el mejor o el peor.
 */

import type { FilaComparativa, MotivoSinRecompra, TipoAudienciaComparativa } from "../types.comparativa";

export type ClaveOrden =
  | "nombre"
  | "fecha"
  | "audiencia"
  | "enviados"
  | "entrega"
  | "lectura"
  | "respuesta"
  | "clics"
  | "bajas"
  | "costo"
  | "costoPorEntregado"
  | "volvieron"
  | "plata";
export type Direccion = "asc" | "desc";
export interface Orden {
  clave: ClaveOrden;
  dir: Direccion;
}

export const ORDEN_POR_DEFECTO: Orden = { clave: "fecha", dir: "desc" };

const TEXTOS: ReadonlySet<ClaveOrden> = new Set<ClaveOrden>(["nombre", "audiencia"]);

/** El primer clic en una columna: los textos de la A a la Z, lo demás de mayor a menor (la más nueva, la que más entregó). */
export function direccionInicial(clave: ClaveOrden): Direccion {
  return TEXTOS.has(clave) ? "asc" : "desc";
}

/** El orden después de tocar el título de `clave`: la misma columna invierte; otra empieza en su dirección inicial. */
export function siguienteOrden(actual: Orden, clave: ClaveOrden): Orden {
  if (actual.clave === clave) return { clave, dir: actual.dir === "asc" ? "desc" : "asc" };
  return { clave, dir: direccionInicial(clave) };
}

export const TIPO_AUDIENCIA: Record<TipoAudienciaComparativa, string> = {
  activa: "Análisis de ventas",
  base: "Público de la base",
  publico: "Público de Activa",
};

/**
 * ¿Hay una recompra con números de verdad? Medida sin las compras de después, los ceros no lo son; con la base de
 * ventas y nadie de los que recibieron en ella, tampoco (no se puede saber si compraron).
 */
export function recompraMedida(f: FilaComparativa): Extract<FilaComparativa["recompra"], { disponible: true }> | null {
  const r = f.recompra;
  if (!r || !r.disponible || r.pendienteBase) return null;
  return r.fuente === "base-de-ventas" && !(Number(r.enLaBase) > 0) ? null : r;
}

/** El valor por el que se ordena; null = sin dato (va al final). */
export function valorOrden(f: FilaComparativa, clave: ClaveOrden): number | string | null {
  const n = (x: unknown) => (typeof x === "number" && Number.isFinite(x) ? x : null);
  switch (clave) {
    case "nombre":
      return f.nombre || "";
    case "fecha": {
      const t = f.iniciadaAt ? Date.parse(f.iniciadaAt) : NaN;
      return Number.isFinite(t) ? t : null;
    }
    case "audiencia":
      return `${TIPO_AUDIENCIA[f.audiencia?.tipo] ?? ""} ${f.audiencia?.nombre ?? ""}`.trim();
    case "enviados":
      return n(f.enviados);
    case "entrega":
    case "bajas":
      return f.enviados > 0 ? n(f.tasas?.[clave]) : null;
    case "lectura":
    case "respuesta":
      return f.entregados > 0 ? n(f.tasas?.[clave]) : null;
    case "clics":
      return f.clics?.activo ? n(f.clics.tasaClicPct) : null;
    case "costo":
      return n(f.cobradoClienteUSD);
    case "costoPorEntregado":
      return n(f.costoPorEntregadoUSD);
    case "volvieron":
      return recompraMedida(f)?.tasa ?? null;
    case "plata":
      return recompraMedida(f)?.plata ?? null;
  }
}

export function ordenarComparativa(filas: FilaComparativa[] | null | undefined, orden: Orden): FilaComparativa[] {
  const signo = orden.dir === "asc" ? 1 : -1;
  const fecha = (f: FilaComparativa) => (f.iniciadaAt ? Date.parse(f.iniciadaAt) || 0 : 0);
  return [...(filas || [])].sort((a, b) => {
    const x = valorOrden(a, orden.clave);
    const y = valorOrden(b, orden.clave);
    if (x === null && y !== null) return 1;
    if (y === null && x !== null) return -1;
    if (x !== null && y !== null && x !== y) {
      const c = typeof x === "string" || typeof y === "string" ? String(x).localeCompare(String(y), "es") : (x as number) - (y as number);
      if (c !== 0) return c * signo;
    }
    // empate: la más nueva primero, y por nombre
    return fecha(b) - fecha(a) || String(a.nombre).localeCompare(String(b.nombre), "es");
  });
}

/** Un porcentaje que ya viene en % (66,7 → «66,7 %»), como `tasaClicPct` del detalle. */
export function pctTexto(p: number | null | undefined): string {
  if (p === null || p === undefined || !Number.isFinite(Number(p))) return "—";
  return `${(Math.round(Number(p) * 10) / 10).toLocaleString("es-AR")} %`;
}

/** Por qué una campaña no tiene «volvieron a comprar», en palabras cortas (el texto largo de la medición va de título). */
export const SIN_RECOMPRA: Record<MotivoSinRecompra, string> = {
  EN_ENVIO: "Se mide cuando termina el envío",
  SIMULADO: "Lo simulado no se mide",
  SIN_BASE_POSTERIOR: "Faltan las compras de después del envío",
  SIN_ENVIOS: "No le llegó a nadie: no hay compras que medir",
  SIN_BASE_DE_VENTAS: "No hay una base de ventas en uso",
  BASE_DE_VENTAS_ANTERIOR: "La base de ventas en uso es de antes del envío",
  SIN_MEDIR: "Todavía no se midió",
};

/** Las bases de cada columna: las mismas que el detalle de la campaña y Estadísticas. */
export const NOTA_BASES_COMPARATIVA =
  "Entregados y bajas, sobre los enviados; leídos, respondieron y clics (personas que tocaron el botón), sobre los entregados: como en el detalle de cada campaña.";
/** Con la base de ventas (un público de la Base o el Público de Activa), como la tarjeta del Resultado. */
export const NOTA_BASE_DE_VENTAS_COMPARATIVA =
  "Con un público de la Base o el Público de Activa, «volvieron a comprar» se mide con la base de ventas en uso y la tasa es sobre los que están en ella: de los demás no se puede saber si compraron.";
export const NOTA_MONEDAS_COMPARATIVA =
  "El costo es lo que se cobró, en dólares; la plata de los que volvieron a comprar va en pesos. Por eso no se dividen entre sí.";
export const NOTA_PERIODO_COMPARATIVA = "Entran las campañas que empezaron a mandar en estas fechas, con los números de toda la campaña.";
